// Finanzen-Panels des Mieters (Handoff Mieter §6: Mietkonto, Gesamtmiete, Kaution).
// Alle Zahlungswerte kommen aus dem gemeinsamen Mietkonto (LedgerEntry) – hier wird nur aufbereitet.

import { eur } from '../immobilien/format';
import { deDatum } from '../immobilien/objektDetail';
import { leseZahl } from '../immobilien/mietverhaeltnisFormular';
import { RUECKSTAND_SCHWELLE } from '../mietkonto/mietkonto';
import { QUELLE_LABEL, type LedgerEntry } from '../mietkonto/typen';
import type { PipFarbe } from './liste';

const runde = (n: number): number => Math.round(n * 100) / 100;
const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** „Okt 2026“ aus „2026-10-01“. */
export function monatLang(monat: string): string {
  const [j, m] = monat.split('-').map(Number);
  return j && m ? `${MONATE[m - 1] ?? ''} ${j}` : monat;
}

export interface KontoKennzahlen { saldo: number; soll12: number; ist12: number }

/** KPIs: Saldo (seit Beginn des Mietkontos), Soll und Eingang der letzten 12 Monate. */
export function kontoKennzahlen(l: Pick<LedgerEntry, 'months' | 'saldo'>): KontoKennzahlen {
  return {
    saldo: l.saldo,
    soll12: runde(l.months.reduce((s, m) => s + m.soll, 0)),
    ist12: runde(l.months.reduce((s, m) => s + m.ist, 0)),
  };
}

export interface MonatsBalken { monat: string; kurz: string; hoehe: number; farbe: PipFarbe; titel: string }

/** „Zahlungseingang je Monat“: Höhe = Ist/Soll (mind. 6 % sichtbar), Farben wie die Monats-Pips. */
export function eingangBalken(l: Pick<LedgerEntry, 'months'>): MonatsBalken[] {
  return l.months.map(m => {
    const farbe: PipFarbe = m.soll <= 0 ? 'vor' : m.ist >= m.soll - 0.005 ? 'ok' : m.ist > 0 ? 'warn' : 'bad';
    const hoehe = m.soll > 0 ? Math.max(6, Math.min(100, Math.round((m.ist / m.soll) * 100))) : 6;
    const [, mm] = m.monat.split('-').map(Number);
    return { monat: m.monat, kurz: MONATE[(mm ?? 1) - 1] ?? '', hoehe, farbe, titel: `${monatLang(m.monat)}: Soll ${eur(m.soll)} · Ist ${eur(m.ist)}` };
  });
}

export interface BuchungsZeile { key: string; titel: string; sub: string; rechts: string; ton: 'ok' | 'bad' | 'info' }

/** „Buchungen“: erfasste Zahlungen zuerst (neueste oben), dann die Monate (neueste oben). */
export function buchungsZeilen(l: Pick<LedgerEntry, 'months' | 'pays'>, art: 'Miete' | 'Hausgeld' = 'Miete'): BuchungsZeile[] {
  const zahlungen: BuchungsZeile[] = l.pays.map(p => ({
    key: `z-${p.id}`, titel: 'Zahlung erfasst', sub: `${deDatum(p.d)} · ${QUELLE_LABEL[p.src]}`, rechts: eur(p.b, { vorzeichen: true }), ton: 'info',
  }));
  const monate: BuchungsZeile[] = [...l.months].reverse().filter(m => m.soll > 0).map(m => {
    const diff = runde(m.ist - m.soll);
    return {
      key: `m-${m.monat}`, titel: `${art} ${monatLang(m.monat)}`, sub: `Soll ${eur(m.soll)} · Ist ${eur(m.ist)}`,
      rechts: diff < -0.005 ? eur(diff) : 'bezahlt', ton: diff < -0.005 ? 'bad' : 'ok',
    };
  });
  return [...zahlungen, ...monate];
}

/** Betrag vorbelegen: offener Rückstand, sonst die Monatsmiete. */
export function zahlungVorschlag(l: Pick<LedgerEntry, 'offen' | 'monthly'>): number {
  return l.offen > RUECKSTAND_SCHWELLE ? l.offen : l.monthly;
}

export function zahlungFehler(betragText: string, datum: string, heute: string): string | null {
  const b = leseZahl(betragText);
  if (b === null || b <= 0) return 'Bitte einen Betrag größer als 0 eingeben.';
  if (!datum) return 'Bitte das Eingangsdatum angeben.';
  if (datum > heute) return 'Das Eingangsdatum liegt in der Zukunft.';
  return null;
}

/** „3 × Kalt“ / „2,5 × Kalt“; ohne Kaltmiete oder Kaution „–“. */
export function kautionHoehe(kaution: number, kalt: number): string {
  if (kaution <= 0 || kalt <= 0) return '–';
  const f = Math.round((kaution / kalt) * 10) / 10;
  return `${String(f).replace('.', ',')} × Kalt`;
}

export interface KautionsRate { nr: number; betrag: number; status: 'eingegangen' | 'teilweise' | 'offen'; faellig: string }

/**
 * Raten der Kaution (§ 551 BGB: höchstens drei, die erste mit Mietbeginn). Anzahl aus kaution_raten,
 * sonst eine Zahlung. Status aus dem eingegangenen Betrag (kaution_ist), der Reihe nach verrechnet –
 * Datum je Rate wird nicht gespeichert.
 */
export function kautionsRaten(soll: number, ist: number, anzahlRoh: unknown): KautionsRate[] {
  if (soll <= 0) return [];
  const a = Math.round(Number(anzahlRoh));
  const anzahl = Number.isFinite(a) && a >= 1 ? Math.min(3, a) : 1;
  const teil = runde(soll / anzahl);
  let rest = Math.max(0, ist);
  return Array.from({ length: anzahl }, (_, i) => {
    const betrag = i === anzahl - 1 ? runde(soll - teil * (anzahl - 1)) : teil;
    const gedeckt = Math.min(betrag, rest);
    rest = runde(rest - gedeckt);
    const status: KautionsRate['status'] = gedeckt >= betrag - 0.005 ? 'eingegangen' : gedeckt > 0 ? 'teilweise' : 'offen';
    return { nr: i + 1, betrag, status, faellig: i === 0 ? 'fällig mit Mietbeginn' : `fällig mit ${i + 1}. Miete` };
  });
}
